/** Twirl — angular rotation that decays with radial distance.
 *
 *  Photoshop's classic Twirl effect: pixels near the center rotate
 *  by the full `angle`, pixels near the corners rotate by zero, with
 *  a smooth falloff in between. Reads as a swirl when applied; the
 *  filter is useful for stylized effects and as a building block for
 *  more elaborate compositional distortions.
 *
 *  Falloff is linear in normalized radius — `rotation = angle · (1 −
 *  r/maxR)` where `r` is distance from center and `maxR` is the
 *  half-diagonal. The center sees the full angle; the corners see
 *  zero. Bilinear sampling on the source pull avoids aliasing.
 *
 *  The angle slider accepts ±720° (two full turns each direction)
 *  because tighter swirls look more dramatic and the math itself
 *  imposes no upper bound. */

import { bilinearSample } from './_bilinear';

export interface TwirlValue {
  /** Twirl angle in degrees. 0 = identity; positive = clockwise. */
  angle: number;
}

export const identityTwirlValue = (): TwirlValue => ({ angle: 0 });

export function applyTwirlPixels(source: ImageData, value: TwirlValue): ImageData {
  const w = source.width;
  const h = source.height;
  const angleDeg = Math.max(-720, Math.min(720, value.angle));
  if (angleDeg === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const maxAngle = (angleDeg * Math.PI) / 180;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      // Falloff: 1 at the center, 0 at the corner. Clamped at 0 for
      // points outside the inscribed-half-diagonal circle.
      const t = Math.max(0, 1 - r / halfDiag);
      const localAngle = maxAngle * t;
      const c = Math.cos(localAngle);
      const s = Math.sin(localAngle);
      // Rotate the OUTPUT position back to find the source position
      // (forward-map produces holes; this is the standard "inverse
      // map" approach).
      const sx = cx + dx * c - dy * s;
      const sy = cy + dx * s + dy * c;
      bilinearSample(src, w, h, sx, sy, out, (y * w + x) * 4);
    }
  }
  return new ImageData(out, w, h);
}

export function applyTwirlToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: TwirlValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyTwirlPixels(source, value), 0, 0);
}
